import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import {
  isLotRecomputeSettled,
  LotRecomputeFinalState,
  LotRecomputeJob,
  resolveLotRecomputePoll,
} from '~/app/accounting/lot-recompute'
import { QUERY_KEYS } from '~/constants/react-query'
import {
  REFETCH_LOT_RECOMPUTE_JOB,
  REFETCH_LOT_RECOMPUTE_JOB_IDLE,
} from '~/constants/refetch-intervals'
import { useRequiredActiveOrg } from '~/hooks/org'
import { useServerRequests } from '~/hooks/useRequests'
import { sendTrackingData } from '~/utils/tracking'

// Retries at the polling cadence, then gives up until a focus or reconnect.
const MAX_JOB_POLL_ERRORS = 10

// Every query the rebuild rewrites, including the gains column on transactions.
const REBUILT_QUERY_KEYS = [
  QUERY_KEYS.ORG_ACCOUNTING_LOTS,
  QUERY_KEYS.ORG_ACCOUNTING_LOT_DISPOSALS,
  QUERY_KEYS.ORG_ACCOUNTING_LOT_ASSERTIONS,
  QUERY_KEYS.ORG_ACCOUNTING_ITEMS,
]

const announceSettled = (job: LotRecomputeJob) => {
  if (job.status === 'failed') {
    toast.error('Lot recompute failed', {
      description: 'Your edits are saved. Try again in a few minutes.',
    })
    return
  }
  // Completed jobs are pruned after a short retention while failures are
  // kept, so a job that is gone by the time it's read (`unknown`) finished.
  const { failed = 0, partitions } = job.result ?? {}
  toast.success('Lots recomputed', {
    description:
      failed > 0 && partitions
        ? `${failed} of ${partitions} tokens could not be computed`
        : 'Lots and gains now reflect your latest edits',
  })
}

export type LotRecomputeState = ReturnType<typeof useLotRecompute>

/**
 * Mount once per page; consumers read the returned state. The rebuild is found
 * through the server's per-org lookup rather than a remembered job id, so this
 * also follows rebuilds it didn't start (an assertion write, the nightly
 * sweep), picking them up on window focus or on the next invalidation.
 */
export const useLotRecompute = ({ enabled }: { enabled: boolean }) => {
  const org = useRequiredActiveOrg()
  const queryClient = useQueryClient()
  const { serverGetRequest, serverPutRequest } = useServerRequests()

  const queryKey = [QUERY_KEYS.ORG_ACCOUNTING_LOT_RECOMPUTE, org.id]

  const fetchJob = async (jobId: string, signal: AbortSignal) => {
    const response = await serverGetRequest<LotRecomputeFinalState>({
      path: `/api/org/accounting/lots/recompute/${encodeURIComponent(jobId)}`,
      params: { orgId: org.id },
      signal,
    })
    if (!response) throw new Error('Failed to fetch recompute status')
    return response.data
  }

  const { data, isError, refetch } = useQuery({
    queryKey,
    enabled,
    queryFn: async ({ signal }): Promise<LotRecomputeJob | null> => {
      const followed =
        queryClient.getQueryData<LotRecomputeJob | null>(queryKey) ?? null

      const response = await serverGetRequest<{
        job: LotRecomputeJob | null
      }>({
        path: '/api/org/accounting/lots/recompute',
        params: { orgId: org.id },
        signal,
      })
      if (!response) throw new Error('Failed to fetch recompute status')

      const { job, settled } = await resolveLotRecomputePoll({
        followed,
        current: response.data.job,
        readFinalState: (jobId) => fetchJob(jobId, signal),
      })
      // Announced from the poll that observed the transition, so it fires once
      // without a second copy of the job to diff against.
      if (settled) {
        announceSettled(settled)
        REBUILT_QUERY_KEYS.forEach((key) =>
          queryClient.invalidateQueries({ queryKey: [key] }),
        )
      }
      return job
    },
    retry: MAX_JOB_POLL_ERRORS,
    retryDelay: REFETCH_LOT_RECOMPUTE_JOB,
    // A cached copy from an earlier visit would be followed as if it were current.
    gcTime: 0,
    // A queued job has no progress to show, so it is checked less often. A
    // hidden tab stops polling and catches up on focus.
    refetchInterval: (query) => {
      if (query.state.data === null) return false
      if (query.state.status === 'error') return false
      return query.state.data?.status === 'active'
        ? REFETCH_LOT_RECOMPUTE_JOB
        : REFETCH_LOT_RECOMPUTE_JOB_IDLE
    },
  })

  const { mutate: recompute, isPending: isStarting } = useMutation({
    mutationFn: async () => {
      const response = await serverPutRequest<{ jobId: string | null }>({
        path: '/api/org/accounting/lots/recompute',
        params: { orgId: org.id },
        errorMessage: 'Failed to start lot recompute',
      })
      // The request helper already toasted; throwing just fails the mutation.
      if (!response) throw new Error('Recompute request failed')
      return response.data.jobId
    },
    onSuccess: (jobId) => {
      sendTrackingData('accountingLotsRecomputeRequested', { orgId: org.id })
      const followed =
        queryClient.getQueryData<LotRecomputeJob | null>(queryKey) ?? null
      // A dedup'd request returns the job already in flight, which may be one
      // the page hadn't seen yet; only a new job is seeded into the poll.
      const isNewJob = jobId !== null && jobId !== followed?.jobId
      toast.success(
        isNewJob ? 'Recompute queued' : 'Recompute already running',
        {
          description: 'Lots and gains update when it finishes',
        },
      )
      // Seeding is what lets a rebuild that finishes inside one poll interval
      // still be announced: the next poll sees the job it followed is gone.
      if (isNewJob) {
        queryClient.setQueryData<LotRecomputeJob>(queryKey, {
          jobId,
          status: 'waiting',
          progress: null,
        })
      }
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const job = data ?? null
  const isRunning = job !== null && !isLotRecomputeSettled(job)

  return {
    job,
    isRunning,
    isStarting,
    recompute,
    hasPollingFailed: isError,
    refetch,
  }
}
